import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  createCuratedArticleSource,
  deleteCuratedArticleSource,
  getCuratedArticleSources,
  isCuratedArticlesApiSuccess,
  getCuratedArticlesPayload,
  updateCuratedArticleSource,
} from '../../services/curatedArticlesApi';

const emptyForm = {
  label: '',
  domainPattern: '',
  enabled: true,
  sortOrder: 10,
};

function SourceSites() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSessionExpired = () => {
    showSnackbar('Session expired. Please login again.', 'error');
    setTimeout(() => {
      window.location = '/login';
    }, 2000);
  };

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await getCuratedArticleSources();
      if (res.statusCode === '10003') {
        handleSessionExpired();
        return;
      }
      if (isCuratedArticlesApiSuccess(res)) {
        const payload = getCuratedArticlesPayload(res);
        setSources(Array.isArray(payload) ? payload : payload?.sources || []);
      } else {
        setSources([]);
        showSnackbar(res.message || 'Failed to load sources', 'error');
      }
    } catch {
      setSources([]);
      showSnackbar('Failed to load sources', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const openCreate = () => {
    setEditingSource(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (source) => {
    setEditingSource(source);
    setForm({
      label: source.label || '',
      domainPattern: source.domainPattern || '',
      enabled: source.enabled !== false,
      sortOrder: source.sortOrder ?? 10,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim() || !form.domainPattern.trim()) {
      showSnackbar('Label and domain pattern are required', 'error');
      return;
    }

    setLoading(true);
    const body = {
      label: form.label.trim(),
      domainPattern: form.domainPattern.trim(),
      enabled: form.enabled,
      sortOrder: Number(form.sortOrder) || 0,
    };

    const res = editingSource
      ? await updateCuratedArticleSource(editingSource.id, body)
      : await createCuratedArticleSource(body);

    setLoading(false);

    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar(editingSource ? 'Source updated' : 'Source created');
      setDialogOpen(false);
      fetchSources();
    } else {
      showSnackbar(res.message || 'Failed to save source', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this source site?')) return;
    const res = await deleteCuratedArticleSource(id);
    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar('Source deleted');
      fetchSources();
    } else {
      showSnackbar(res.message || 'Failed to delete', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Source Sites</Typography>
          <Typography variant="body2" color="text.secondary">
            Whitelisted domains for scraping. If none are enabled, any URL is allowed.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Add Source
        </Button>
      </Box>

      {loading && !sources.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Domain Pattern</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Sort Order</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No source sites configured — all URLs allowed
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sources.map((source) => (
                  <TableRow key={source.id} hover>
                    <TableCell>{source.label}</TableCell>
                    <TableCell>
                      <code>{source.domainPattern}</code>
                    </TableCell>
                    <TableCell>{source.enabled ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{source.sortOrder}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(source)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(source.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSource ? 'Edit Source' : 'Add Source'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Domain Pattern"
                placeholder="espncricinfo.com"
                value={form.domainPattern}
                onChange={(e) => setForm((f) => ({ ...f, domainPattern: e.target.value }))}
                helperText="Matches hostname or subdomain"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                }
                label="Enabled"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SourceSites;
